import { Button } from '@heidi/design-system/button';
import { DrawerClose, DrawerTitle } from '@heidi/design-system/drawer';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { ArrowLeft, ChevronRight, X } from 'lucide-react';
import { forwardRef, type MouseEvent, type ReactNode, type Ref } from 'react';
import { useIntl } from 'react-intl';

type MobileDrawerHeaderProps = {
  title: string;
  badge?: number;
  onBack?: () => void;
  /** Full data-testid for the back button. When omitted, no testid is set. */
  backButtonTestId?: string;
  /** Ref forwarded to the back button element, e.g. for programmatic focus. */
  backButtonRef?: Ref<HTMLButtonElement>;
};

export const MobileDrawerHeader = ({
  title,
  badge,
  onBack,
  backButtonTestId,
  backButtonRef,
}: MobileDrawerHeaderProps) => {
  const intl = useIntl();

  return (
    <div className="flex items-center justify-between border-b border-border px-4 py-3">
      {onBack ? (
        <Button
          ref={backButtonRef}
          variant="ghost"
          size="sm"
          iconOnly
          disableAutomaticSvgSizing
          data-testid={backButtonTestId}
          aria-label={intl.formatMessage({ defaultMessage: 'Back', id: 'cyR7KhiuaU' })}
          onClick={onBack}
        >
          <ArrowLeft size={18} aria-hidden="true" className="rtl:-scale-x-100" />
        </Button>
      ) : (
        <div className="w-8" /> // mirrors close-button width (size="sm" iconOnly) so the title stays centred
      )}
      <DrawerTitle className="flex items-center gap-1.5">
        {title}
        {badge !== undefined && badge > 0 && (
          <span className="flex size-5 items-center justify-center rounded-full bg-background-secondary">
            <TypographyCaption>{badge}</TypographyCaption>
          </span>
        )}
      </DrawerTitle>
      <DrawerClose asChild>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          disableAutomaticSvgSizing
          aria-label={intl.formatMessage({ defaultMessage: 'Close', id: 'rbrahOGMC3' })}
        >
          <X size={18} aria-hidden="true" />
        </Button>
      </DrawerClose>
    </div>
  );
};

type MobileFilterRowProps = {
  icon?: ReactNode;
  label: string;
  value: string | null;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  testId?: string;
};

export const MobileFilterRow = forwardRef<HTMLButtonElement, MobileFilterRowProps>(
  ({ icon, label, value, onClick, testId }, ref) => (
    <Button
      size="lg"
      ref={ref}
      variant="ghost"
      onClick={onClick}
      data-testid={testId}
      className="flex h-auto w-full items-center justify-between rounded-none px-4 py-3 text-start"
    >
      <div className="flex min-w-0 items-center gap-3">
        {icon && (
          <span aria-hidden="true" className="shrink-0 text-text-secondary">
            {icon}
          </span>
        )}
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <TypographyP2Bold>{label}</TypographyP2Bold>
          {/* Summaries can name the selection, which outruns the row. */}
          {value && (
            <TypographyCaption className="w-full truncate text-start text-text-secondary">
              {value}
            </TypographyCaption>
          )}
        </div>
      </div>
      <ChevronRight
        size={18}
        aria-hidden="true"
        className="shrink-0 text-text-secondary rtl:-scale-x-100"
      />
    </Button>
  )
);
MobileFilterRow.displayName = 'MobileFilterRow';
